import {
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useMemo } from "react";
import dayjs from "dayjs";
import { useAppSelector } from "store/store";
import { selectScrapViewportRange } from "store/slices/scrapTrackingViewportSlice";
import { useTenant } from "hooks/settings";
import { useTenantTranslation } from "hooks/formatters";
import {
  type ModelCoverageDataKind,
  type ModelCoverageWindow,
  useGetModelCoverageConfigQuery,
} from "src/store/api/generatedApi";
import { useModelCoverageData } from "../modelCoverage/hooks/useModelCoverageData";
import { ModelCoverageTimeline } from "../modelCoverage/ModelCoverageTimeline";
import { PERIOD_DURATION_SECONDS } from "../components/timelineChartUtils";
import { SummaryChip } from "./SummaryChip";

type ModelCoverageSectionProps = Readonly<{
  selectedLocation: string;
  selectedLabellingName: string | null;
  onModelChange: (labellingName: string | null) => void;
}>;

export const ModelCoverageSection = ({
  selectedLocation,
  onModelChange,
  selectedLabellingName,
}: ModelCoverageSectionProps) => {
  const tenant = useTenant();
  const { t } = useTenantTranslation();
  const viewportRange = useAppSelector(selectScrapViewportRange);

  const start = viewportRange.start;
  const end = viewportRange.end;

  const {
    data: coverageConfig,
    isLoading: isLoadingConfig,
    isError: isConfigError,
  } = useGetModelCoverageConfigQuery({ tenantName: tenant });

  const modelsByLocation = useMemo(
    () => coverageConfig?.available_models ?? {},
    [coverageConfig?.available_models]
  );

  const availableModels = useMemo(
    () => (selectedLocation ? (modelsByLocation[selectedLocation] ?? []) : []),
    [modelsByLocation, selectedLocation]
  );

  const { windows, isLoading: isLoadingData } = useModelCoverageData(
    start,
    end,
    selectedLocation,
    PERIOD_DURATION_SECONDS,
    selectedLabellingName
  );

  const summary = useMemo(() => {
    const hasKind = (
      w: (typeof windows)[number],
      kind: ModelCoverageDataKind
    ) => w.available_data.includes(kind);
    const total = windows.length;
    const withVideo = windows.filter((w) => hasKind(w, "video")).length;
    const withTransformed = windows.filter((w) =>
      hasKind(w, "transformed")
    ).length;
    const withEmbedded = windows.filter((w) => hasKind(w, "embedded")).length;
    const withPredictions = windows.filter((w) =>
      hasKind(w, "predictions")
    ).length;
    const withLabels = windows.filter((w) => hasKind(w, "model_labels")).length;
    return {
      total,
      withVideo,
      withTransformed,
      withEmbedded,
      withPredictions,
      withLabels,
    };
  }, [windows]);

  const handleWindowClick = useCallback(
    (coverageWindow: ModelCoverageWindow) => {
      if (!selectedLocation) return;
      const windowStart = dayjs.utc(coverageWindow.window_start);
      const date = windowStart.format("YYYY-MM-DD");
      const time = windowStart.toISOString();
      const params = new URLSearchParams({ date, time });
      if (selectedLabellingName) params.set("model", selectedLabellingName);
      const url = `/${tenant}/tool/view-labels/${selectedLocation}?${params}`;
      window.open(url, "_blank");
    },
    [tenant, selectedLocation, selectedLabellingName]
  );

  if (isLoadingConfig) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (isConfigError) {
    return (
      <Stack p={2}>
        <Alert severity="error">{t("modelCoverageConfigError")}</Alert>
      </Stack>
    );
  }

  return (
    <Stack
      spacing={2}
      sx={{
        p: 3,
        backgroundColor: "grey.50",
        borderRadius: 2,
        border: 1,
        borderColor: "grey.300",
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="h5" fontWeight={600} color="text.primary">
          {t("modelCoverageTitle")}
        </Typography>

        <Select
          size="small"
          value={selectedLabellingName ?? ""}
          onChange={(event) => {
            onModelChange(event.target.value);
          }}
        >
          {availableModels.map((model) => (
            <MenuItem key={model.labelling_name} value={model.labelling_name}>
              {model.labelling_name || model.mlflow_run_id}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      <Stack spacing={2}>
        <Stack direction="row" spacing={4}>
          <SummaryChip label={t("modelCoverageTotal")} value={summary.total} />
          <SummaryChip
            label={t("modelCoverageHasVideo")}
            value={summary.withVideo}
          />
          <SummaryChip
            label={t("modelCoverageTransformed")}
            value={summary.withTransformed}
          />
          <SummaryChip
            label={t("modelCoverageEmbedded")}
            value={summary.withEmbedded}
          />
          <SummaryChip
            label={t("modelCoverageInferenceRun")}
            value={summary.withPredictions}
          />
          <SummaryChip
            label={t("modelCoverageLabelsProduced")}
            value={summary.withLabels}
            color="success.main"
          />
        </Stack>

        {isLoadingData ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress size={24} />
          </Stack>
        ) : windows.length === 0 ? (
          <Alert severity="info">{t("modelCoverageNoData")}</Alert>
        ) : (
          <ModelCoverageTimeline
            windows={windows}
            start={start}
            end={end}
            onWindowClick={handleWindowClick}
          />
        )}
      </Stack>
    </Stack>
  );
};
